import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Container, ContainerFile } from '../../../../core/interfaces/container.interface';
import { useCompanyCore } from '../../../../core/modules/company';
import { useContainerCore } from '../../../../core/modules/container';
import Spinner from '../../Spinner';
import Swal from 'sweetalert2';

interface UploadImagesDialogProps {
  open: boolean;
  onClose: () => void;
  container: Container | null;
  onSuccess?: () => void;
}

const MAX_CONTAINER_FILES = 10;

const UploadImagesDialog = ({ open, onClose, container, onSuccess }: UploadImagesDialogProps) => {
  const { companyId } = useCompanyCore();
  const { postContainerFile, deleteContainerFile } = useContainerCore();
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingFileIds, setDeletingFileIds] = useState<Set<number>>(new Set());
  // Estado local para las imágenes existentes que se actualiza cuando se eliminan
  const [localExistingFiles, setLocalExistingFiles] = useState<ContainerFile[]>([]);

  // Sincronizar el estado local cuando cambia el container o se abre el diálogo
  useEffect(() => {
    if (open && container?.files) {
      setLocalExistingFiles(container.files);
    } else if (!open) {
      // Limpiar cuando se cierra el diálogo
      setLocalExistingFiles([]);
    }
  }, [container?.files, open]);

  const existingFiles = useMemo(() => localExistingFiles, [localExistingFiles]);
  const totalFilesCount = useMemo(() => existingFiles.length + newFiles.length, [existingFiles.length, newFiles.length]);
  const canAddMore = useMemo(() => totalFilesCount < MAX_CONTAINER_FILES, [totalFilesCount]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const remainingSlots = MAX_CONTAINER_FILES - totalFilesCount;
      const filesToAdd = files.slice(0, remainingSlots);
      setNewFiles([...newFiles, ...filesToAdd]);
      // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
      e.target.value = '';
    }
  }, [newFiles, totalFilesCount]);

  const handleRemoveNewFile = useCallback((index: number) => {
    const updatedFiles = newFiles.filter((_, i) => i !== index);
    setNewFiles(updatedFiles);
  }, [newFiles]);

  const handleDeleteExistingFile = useCallback(async (file: ContainerFile) => {
    if (!companyId || !container) {
      return;
    }

    const result = await Swal.fire({
      title: '¿Eliminar imagen?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) {
      return;
    }

    setDeletingFileIds((prev) => new Set(prev).add(file.id));
    
    deleteContainerFile(
      companyId,
      container.id,
      file.id,
      () => {
        // Actualizar el estado local inmediatamente para reflejar la eliminación en la UI
        setLocalExistingFiles((prev) => prev.filter((f) => f.id !== file.id));
        setDeletingFileIds((prev) => {
          const next = new Set(prev);
          next.delete(file.id);
          return next;
        });
        // Llamar a onSuccess para refrescar la lista completa en el componente padre
        onSuccess?.();
      },
      () => {
        setDeletingFileIds((prev) => {
          const next = new Set(prev);
          next.delete(file.id);
          return next;
        });
        Swal.fire({
          title: 'Error',
          text: 'No se pudo eliminar la imagen',
          icon: 'error',
        });
      }
    );
  }, [companyId, container, deleteContainerFile, onSuccess]);

  const handleSubmit = useCallback(async () => {
    if (!companyId || !container || newFiles.length === 0) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failureCount = 0;

    // Subir cada archivo secuencialmente
    for (const file of newFiles) {
      try {
        await new Promise<void>((resolve, reject) => {
          postContainerFile(
            companyId,
            container.id,
            file,
            () => {
              successCount++;
              resolve();
            },
            () => {
              failureCount++;
              reject(new Error('Error al subir archivo'));
            }
          );
        });
      } catch (error) {
        // Error ya manejado en el callback, continuar con el siguiente archivo
        console.error('Error al subir archivo:', error);
      }
    }

    setLoading(false);

    // Si al menos una imagen se subió correctamente, considerar éxito
    if (successCount > 0) {
      setNewFiles([]);
      onSuccess?.();
      onClose();
    }
  }, [companyId, container, newFiles, postContainerFile, onSuccess, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setNewFiles([]);
      setLocalExistingFiles([]);
      onClose();
    }
  }, [loading, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px] md:min-w-[600px]',
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight="bold">
            Subir Imágenes del Contenedor
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
          >
            ✕
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <div className="flex flex-col py-16">
          {container && (
            <Box mb={3}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Contenedor: <strong>{container.code}</strong>
              </Typography>
              {container.description && (
                <Typography variant="body2" color="text.secondary">
                  Descripción: <strong>{container.description}</strong>
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="subtitle1" className="mb-16 font-semibold">
            Imágenes del Contenedor (Opcional)
          </Typography>
          <Typography variant="caption" color="text.secondary" className="mb-16 block">
            Máximo {MAX_CONTAINER_FILES} imágenes ({totalFilesCount}/{MAX_CONTAINER_FILES} usadas)
          </Typography>

          {/* Imágenes existentes */}
          {existingFiles.length > 0 && (
            <Box mb={3}>
              <Typography variant="body2" color="text.secondary" className="mb-8">
                Imágenes existentes ({existingFiles.length})
              </Typography>
              <div className="flex flex-wrap gap-12">
                {existingFiles.map((file) => (
                  <div
                    key={file.id}
                    className="relative w-[100px] h-[100px] rounded shadow overflow-hidden border-2 border-blue-300"
                  >
                    <img
                      src={file.url}
                      alt={`Imagen ${file.id}`}
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100?text=Error';
                      }}
                    />
                    <Chip
                      label="Existente"
                      size="small"
                      className="absolute top-0 left-0 m-4"
                      sx={{
                        backgroundColor: 'rgba(33, 150, 243, 0.9)',
                        color: 'white',
                        fontSize: '0.65rem',
                        height: '20px',
                      }}
                    />
                    <IconButton
                      color="error"
                      size="small"
                      aria-label="delete picture"
                      className="absolute top-0 right-0"
                      onClick={() => handleDeleteExistingFile(file)}
                      disabled={loading || deletingFileIds.has(file.id)}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                    >
                      {deletingFileIds.has(file.id) ? (
                        <Spinner show={true} />
                      ) : (
                        <HighlightOffIcon fontSize="small" />
                      )}
                    </IconButton>
                  </div>
                ))}
              </div>
            </Box>
          )}

          {/* Seleccionar nuevas imágenes */}
          <div className="mb-16">
            <label htmlFor="container-image-input">
              <Button
                color="primary"
                variant="outlined"
                aria-label="upload picture"
                component="span"
                disabled={loading || !canAddMore}
                startIcon={<PhotoCamera />}
              >
                Seleccionar Imágenes
              </Button>
            </label>
            <input
              id="container-image-input"
              type="file"
              accept="image/*"
              className="hidden"
              multiple
              onChange={handleFileSelect}
            />
          </div>

          {/* Nuevas imágenes seleccionadas */}
          {newFiles.length > 0 && (
            <Box mb={3}>
              <Typography variant="body2" color="text.secondary" className="mb-8">
                Nuevas imágenes ({newFiles.length})
              </Typography>
              <div className="flex flex-wrap gap-12">
                {newFiles.map((file, index) => (
                  <div
                    key={index}
                    className="relative w-[100px] h-[100px] rounded shadow overflow-hidden border-2 border-green-300"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                      className="object-cover w-full h-full"
                    />
                    <Chip
                      label="Nueva"
                      size="small"
                      className="absolute top-0 left-0 m-4"
                      sx={{
                        backgroundColor: 'rgba(76, 175, 80, 0.9)',
                        color: 'white',
                        fontSize: '0.65rem',
                        height: '20px',
                      }}
                    />
                    <IconButton
                      color="error"
                      size="small"
                      aria-label="delete picture"
                      className="absolute top-0 right-0"
                      onClick={() => handleRemoveNewFile(index)}
                      disabled={loading}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                    >
                      <HighlightOffIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            </Box>
          )}

          <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={3}>
            <Box display="flex" justifyContent="center" gap={2}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                size="large"
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || newFiles.length === 0}
                size="large"
              >
                <Spinner show={loading} />
                {loading ? 'Subiendo...' : `Subir ${newFiles.length} imagen${newFiles.length !== 1 ? 'es' : ''}`}
              </Button>
            </Box>
            {newFiles.length === 0 && totalFilesCount > 0 && (
              <Typography variant="caption" color="text.secondary" className="text-center">
                Puedes eliminar imágenes existentes o agregar nuevas
              </Typography>
            )}
          </Box>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadImagesDialog;

